import React, { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import ArrowDownward from '@mui/icons-material/ArrowDownward';

export type DiscoverySortDir = 'asc' | 'desc';

const registeredCell = (extra?: object) => ({
  minWidth: 0,
  fontSize: '0.78rem',
  lineHeight: 1.35,
  color: '#ccc',
  ...extra,
});

/** A partir de este ancho la lista es tabla. Más angosto, cada equipo es una ficha. */
export const discoveryRegisteredBreak = '799px';

export const discoveryRegisteredGridSx = {
  display: 'grid',
  width: '100%',
  alignItems: 'center',
  columnGap: '16px',
  gridTemplateColumns:
    'minmax(8.5rem, 1.7fr) minmax(3.4rem, 0.6fr) minmax(5.6rem, 0.9fr) minmax(7rem, 1.4fr) minmax(5.4rem, 0.8fr) minmax(6.2rem, 1fr) minmax(5.2rem, 0.8fr)',
};

/** En vertical el recuadro de atrás desaparece y quedan solo las fichas. */
export function discoveryRegisteredShellSx(extra: Record<string, unknown> = {}) {
  return {
    backgroundColor: '#1a1a1a',
    borderRadius: 1,
    p: 1.5,
    border: '1px solid #333',
    ...extra,
    [`@media (max-width: ${discoveryRegisteredBreak})`]: {
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: 0,
      p: 0,
      width: '100%',
      maxWidth: '100%',
      minWidth: 0,
      boxSizing: 'border-box',
    },
  };
}

export const discoveryRegisteredCol = {
  name: registeredCell({ fontSize: '0.84rem', color: '#fff', fontWeight: 600 }),
  type: registeredCell(),
  ip: registeredCell({ fontVariantNumeric: 'tabular-nums' }),
  model: registeredCell(),
  power: registeredCell(),
  status: registeredCell(),
  via: registeredCell(),
};

export const discoveryRegisteredRowSx = {
  ...discoveryRegisteredGridSx,
  py: 0.35,
  borderBottom: '1px solid #2a2a2a',
  '& .discovery-cell-label': { display: 'none' },
  [`@media (max-width: ${discoveryRegisteredBreak})`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gridTemplateColumns: 'minmax(0, 1fr)',
    columnGap: 0,
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    rowGap: '6px',
    px: 1.25,
    py: 1.25,
    mb: 1,
    border: '1px solid #3a3d42',
    borderRadius: '10px',
    backgroundColor: '#2a2d31',
    overflow: 'hidden',
    '& > :first-of-type': {
      display: 'block',
      minWidth: 0,
      maxWidth: '100%',
      fontSize: '0.95rem',
      fontWeight: 600,
      color: '#fff',
      pb: 0.7,
      mb: 0.15,
      borderBottom: '1px solid #3a3d42',
      overflowWrap: 'anywhere',
    },
    '& > :not(:first-of-type)': {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '8px',
      py: 0,
      minWidth: 0,
      maxWidth: '100%',
      boxSizing: 'border-box',
    },
    '& .discovery-cell-label': {
      display: 'block',
      fontSize: '0.62rem',
      lineHeight: 1.15,
      fontWeight: 600,
      letterSpacing: '0.02em',
      color: '#8d8d8d',
      mb: 0,
      flex: '0 0 auto',
    },
    '& > :first-of-type .discovery-cell-label': { display: 'none' },
    '& > :not(:first-of-type) > span:last-of-type': {
      marginLeft: 'auto',
      minWidth: 0,
      textAlign: 'right',
      fontSize: '0.78rem',
      overflowWrap: 'anywhere',
    },
  },
} as const;

export function DiscoveryCell({
  label,
  sx,
  title,
  onClick,
  children,
}: {
  label: string;
  sx?: object;
  title?: string;
  onClick?: () => void;
  children: ReactNode;
}) {
  return (
    <Box
      title={title}
      onClick={onClick}
      sx={{
        minWidth: 0,
        py: 0.55,
        [`@media (max-width: ${discoveryRegisteredBreak})`]: {
          py: 0,
          borderBottom: 'none',
        },
        ...(onClick ? { cursor: 'pointer', WebkitTapHighlightColor: 'transparent' } : {}),
        ...sx,
      }}
    >
      <Box className="discovery-cell-label" component="span">{label}</Box>
      <Box component="span" sx={{ display: 'block', minWidth: 0, maxWidth: '100%', wordBreak: 'break-word' }}>{children}</Box>
    </Box>
  );
}

export interface DiscoverySortColumn<K extends string> {
  key: K;
  label: string;
  sx: object;
}

function compareDiscoveryText(a: string, b: string, dir: DiscoverySortDir): number {
  const cmp = String(a || '').localeCompare(String(b || ''), undefined, {
    sensitivity: 'base',
    numeric: true,
  });
  return dir === 'asc' ? cmp : -cmp;
}

export function useDiscoveryColumnSort<T, K extends string>(
  items: T[],
  getValue: (item: T, key: K) => string,
) {
  const [sort, setSort] = useState<{ key: K; dir: DiscoverySortDir } | null>(null);

  const toggleSort = useCallback((key: K) => {
    setSort((prev) => {
      if (prev?.key === key) {
        return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      return { key, dir: 'asc' };
    });
  }, []);

  const sorted = useMemo(() => {
    if (!sort) return items;
    return [...items].sort((a, b) => (
      compareDiscoveryText(getValue(a, sort.key), getValue(b, sort.key), sort.dir)
    ));
  }, [items, getValue, sort]);

  return {
    sorted,
    sortKey: sort?.key ?? null,
    sortDir: sort?.dir ?? 'asc' as DiscoverySortDir,
    toggleSort,
  };
}

interface HeaderProps<K extends string> {
  columns: DiscoverySortColumn<K>[];
  sortKey: K | null;
  sortDir: DiscoverySortDir;
  onToggle: (key: K) => void;
  fill?: boolean;
  /** Siempre chips, sin pasar por la tabla ancha. Lo usa Cámaras en vertical. */
  chips?: boolean;
}

export function DiscoveryRegisteredTable<K extends string>({
  columns,
  sortKey,
  sortDir,
  onToggle,
  children,
}: HeaderProps<K> & { children: ReactNode }) {
  return (
    <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box', overflowAnchor: 'none' }}>
      <DiscoverySortableHeader
        columns={columns}
        sortKey={sortKey}
        sortDir={sortDir}
        onToggle={onToggle}
        fill
      />
      {children}
    </Box>
  );
}

export function DiscoverySortableHeader<K extends string>({
  columns,
  sortKey,
  sortDir,
  onToggle,
  fill = false,
  chips = false,
}: HeaderProps<K>) {
  const chipSx = {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    gap: '6px',
    mb: 1.25,
    '& > button': {
      position: 'relative',
      flex: '0 0 auto',
      width: 'auto',
      minHeight: 0,
      px: 2,
      py: 0.45,
      border: '1px solid #3a3d42',
      borderRadius: '999px',
      backgroundColor: '#222',
      justifyContent: 'center',
      textAlign: 'center',
    },
    '& .discovery-sort-slot': {
      position: 'absolute',
      left: 8,
      top: '50%',
      transform: 'translateY(-50%)',
    },
    '& .discovery-sort-slot:empty': { display: 'none' },
  };
  return (
    <Box
      sx={chips ? chipSx : fill ? {
        ...discoveryRegisteredGridSx,
        mb: 0.25,
        [`@media (max-width: ${discoveryRegisteredBreak})`]: {
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
          mb: 1.25,
          gridTemplateColumns: 'none',
          '& > button': {
            position: 'relative',
            flex: '0 0 auto',
            width: 'auto',
            minHeight: 0,
            px: 2,
            py: 0.45,
            border: '1px solid #3a3d42',
            borderBottom: '1px solid #3a3d42',
            borderRadius: '999px',
            backgroundColor: '#222',
            justifyContent: 'center',
            textAlign: 'center',
          },
          '& .discovery-sort-slot': {
            position: 'absolute',
            left: 8,
            top: '50%',
            transform: 'translateY(-50%)',
          },
          '& .discovery-sort-slot:empty': { display: 'none' },
        },
      } : {
        display: 'flex',
        alignItems: 'center',
        py: 0.35,
        borderBottom: '1px solid #333',
        mb: 0.25,
        minWidth: 0,
      }}
    >
      {columns.map((col) => {
        const active = col.key === sortKey;
        return (
          <Box
            key={col.key}
            component="button"
            type="button"
            onClick={() => onToggle(col.key)}
            aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
            sx={{
              ...(fill ? { minWidth: 0 } : col.sx),
              appearance: 'none',
              background: 'transparent',
              border: 0,
              borderBottom: fill ? '1px solid #333' : undefined,
              p: 0,
              py: fill ? 0.7 : 0,
              m: 0,
              minHeight: 28,
              overflow: fill ? 'visible' : 'hidden',
              fontSize: '0.65rem',
              fontWeight: 700,
              letterSpacing: '0.03em',
              color: active ? '#2391FF' : '#888',
              cursor: 'pointer',
              userSelect: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 0.35,
              textAlign: 'left',
              WebkitTapHighlightColor: 'transparent',
              '&:hover': { color: active ? '#4aa6ff' : '#bbb' },
            }}
          >
            <Box className="discovery-sort-slot" sx={{ width: 12, height: 12, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {active && (
                sortDir === 'asc'
                  ? <ArrowUpward sx={{ fontSize: 12 }} />
                  : <ArrowDownward sx={{ fontSize: 12 }} />
              )}
            </Box>
            <Typography
              component="span"
              sx={{
                fontSize: 'inherit',
                fontWeight: 'inherit',
                color: 'inherit',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
              }}
            >
              {col.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}
